/**
 * Registers `public/sw.js`, the service worker that makes molt an installable
 * PWA. `docs/notes/pwa.md` is the authority.
 *
 * ⚠ **The gate is the protocol, not `import.meta.env.PROD`.** The root `.env`
 * sets `NODE_ENV=development`, so a local `vite build` is a dev bundle and
 * `PROD` is false on this machine whatever is served — gating on it would make
 * the worker impossible to exercise locally and say nothing about production.
 * Production is the only https origin; dev, preview and every e2e run are
 * plain http on 127.0.0.1, where a registered worker would outlive the run and
 * keep answering for a later build on the same port. `VITE_ENABLE_SW=1` at
 * build time is the deliberate local opt-in.
 *
 * ⚠ **No `vite:preloadError` handler, on purpose.** A long-lived standalone
 * window meets stale chunk names after every deploy, and the obvious fix is a
 * reload-once listener here. It is worse than nothing: `defaultPreload:
 * 'intent'` preloads route chunks on hover and touchstart, so it would reload
 * the page — discarding a half-typed comment — on a preload nobody asked to
 * see; its `preventDefault` resolves the import to `undefined`, which
 * TanStack then reports as a `TypeError` instead of reloading; and a
 * once-per-session flag outlives the second deploy in a window that stays
 * open for days. `lazyRouteComponent` already reloads once on a REAL
 * navigation, keyed per failing chunk (`tanstack_router_reload:<message>`),
 * which is exactly the behaviour wanted.
 */

export interface ServiceWorkerGate {
  readonly protocol: string;
  readonly supported: boolean;
  readonly optIn: boolean;
}

export function shouldRegisterServiceWorker({
  protocol,
  supported,
  optIn,
}: ServiceWorkerGate): boolean {
  return supported && (protocol === 'https:' || optIn);
}

/** The message `sw.js`'s `notificationclick` posts to an open window. */
export function navigateMessageHref(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const record = data as Record<string, unknown>;
  if (record['type'] !== 'molt:navigate') return null;
  const href = record['href'];
  // Same-origin paths only — the worker only ever sends `notificationHref`.
  return typeof href === 'string' && href.startsWith('/') && !href.startsWith('//')
    ? href
    : null;
}

export interface ServiceWorkerEnvironment {
  readonly location: Pick<Location, 'protocol'>;
  readonly serviceWorker: ServiceWorkerContainer | undefined;
  readonly optIn: boolean;
  readonly whenLoaded: (run: () => void) => void;
}

function browserEnvironment(): ServiceWorkerEnvironment {
  return {
    location: window.location,
    serviceWorker: 'serviceWorker' in navigator ? navigator.serviceWorker : undefined,
    optIn: import.meta.env['VITE_ENABLE_SW'] === '1',
    whenLoaded: (run) => {
      if (document.readyState === 'complete') run();
      else window.addEventListener('load', run, { once: true });
    },
  };
}

/**
 * Registers the worker and routes a tapped Android notification.
 *
 * `onNavigate` should be a full document navigation (`location.assign`), the
 * same as the desktop path: a notification's href often carries
 * `#comment-<id>`, and `CommentSection` learns of a hash only on mount or on
 * `hashchange` — which `history.pushState` never fires, so a router
 * navigation to a comment on the card already open would scroll nowhere.
 */
export function installServiceWorker(
  onNavigate: (href: string) => void,
  env: ServiceWorkerEnvironment = browserEnvironment(),
): void {
  const container = env.serviceWorker;
  if (
    container === undefined ||
    !shouldRegisterServiceWorker({
      protocol: env.location.protocol,
      supported: true,
      optIn: env.optIn,
    })
  ) {
    return;
  }

  container.addEventListener('message', (event: MessageEvent) => {
    const href = navigateMessageHref(event.data);
    if (href !== null) onNavigate(href);
  });

  env.whenLoaded(() => {
    container.register('/sw.js', { scope: '/' }).catch(() => {
      // An unregistered worker costs installability and the asset cache,
      // nothing the app depends on — never let it surface as an error.
    });
  });
}
